import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Message, MessageType, SeverityType } from 'app/notifications/message';
import { NotificationsService } from 'app/notifications/notifications.service';
import { ValidationResult } from 'app/shared/models/validation-result';

@Injectable()
export class UtilsService {
  private _programType = 'vb';

  constructor(private _route: ActivatedRoute,
    private _notificationsService: NotificationsService) {
    this._route.queryParams.subscribe((params) => {
      this._programType = params['program'] ? params['program'] : this._programType;
    });
  }

  /**
   * Marks all controls in a form group as touched
   * @param formGroup - The group to caress..hah
   */
  public markFormGroupTouched(formGroup: FormGroup) {
    (<any>Object).values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control.controls) {
        control.controls.forEach(c => this.markFormGroupTouched(c));
      }
    });
  }

  /**
   * show validation messages
   * @param validationErrors 
   */
  public handleValidationErrors(validationResult: ValidationResult): void {
    let errorMessage = '';
    validationResult.errors.forEach(error => {
      errorMessage += `${error.propertyName}: ${error.errorMessage} \n`;
    });

    this._notificationsService.addNotification({
      type: MessageType.Toast,
      severity: SeverityType.Error,
      title: 'Error',
      message: errorMessage
    } as Message);
  }

  /**
   * Returns true if key is not a key in object or object[key] has
   * value undefined. If key is a dot-delimited string of key names,
   * object and its sub-objects are checked recursively.
   */
  public isUndefined(object, key) {
    const keyChain = Array.isArray(key) ? key : key.split('.'),
      objectHasKey = keyChain[0] in object,
      keyHasValue = typeof object[keyChain[0]] !== 'undefined' && object[keyChain[0]] !== null;

    if (objectHasKey && keyHasValue) {
      if (keyChain.length > 1) {
        return this.isUndefined(object[keyChain[0]], keyChain.slice(1));
      }

      return false;
    } else {
      return true;
    }
  }

  /**
   * Check if value is null or undefined
   */
  public isNoU(val: any): boolean {
    return val === null || val === undefined;
  }

  public compare(a, b, isAsc) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  public isDigit(val: any) {
    const chars = '0123456789';
    return chars.indexOf(val.toLowerCase()) > -1;
  }

  public isNullOrEmptyString(val: string): boolean {
    return this.isNoU(val) || val.trim() === '';
  }

  public isRunningInWinShell(): boolean {
    if (this._programType === 'vb') {
      return true;
    }

    return false;
  }

  /**
   * decode from base64
   * @param param 
   * @returns decoded string
   */
  public decode(param: string): string {
    return atob(param);
  }

  /**
   * build url
   * @param url initial url
   * @param enc is encripted
   * @param tabView tab view
   * @param xdb db
   * @param xDataEntry data entry
   * @param xDealerID dealer id
   * @param xEmpType employee type
   * @param xSecurityLevel security level
   * @param xString xstring
   * @param xTimezone time zone
   * @param xUsername employee
   * @param xOrigUserName employee
   * @param program program
   * @param custId customer id
   * @param eid event id
   * @param eventtype event type
   * @param tabtitle tab title
   * @param useCoreView
   * @returns 
   */
  public buildRedirectUrl(url: string, enc: boolean, tabView: number, xdb: string,
    xDataEntry: string, xDealerID: number, xEmpType: string, xSecurityLevel: string, xString: string,
    xTimezone: string, xUsername: number, xOrigUserName: number, program: string, custId: number,
    eid: number, eventtype: number, tabtitle: string, useCoreView: number): string {
    let resultUrl = url;

    if (enc) {
      resultUrl = resultUrl + 'enc=1';
    } else {
      resultUrl = resultUrl + '1=1';
    }

    if (!this.isNoU(tabView)) {
      resultUrl = resultUrl + '&tabview=' + this.encode(tabView.toString(), enc); 
    }

    if (!this.isNullOrEmptyString(xdb)) {
      resultUrl = resultUrl + '&xdb=' + this.encode(xdb, enc); 
    }

    if (!this.isNullOrEmptyString(xDataEntry)) {
      resultUrl = resultUrl + '&xDataEntry=' + this.encode(xDataEntry, enc);
    }

    if (!this.isNoU(xDealerID)) {
      resultUrl = resultUrl + '&xDealerID=' + this.encode(xDealerID.toString(), enc);
    }

    if (!this.isNullOrEmptyString(xEmpType)) {
      resultUrl = resultUrl + '&xEmpType=' + this.encode(xEmpType, enc);
    }

    if (!this.isNullOrEmptyString(xSecurityLevel)) {
      resultUrl = resultUrl + '&xSecurityLevel=' + this.encode(xSecurityLevel, enc);
    }

    if (!this.isNullOrEmptyString(xString)) {
      resultUrl = resultUrl + '&xString=' + xString; 
    }

    if (!this.isNullOrEmptyString(xTimezone)) {
      resultUrl = resultUrl + '&xTimezone=' + this.encode(xTimezone, enc);
    }

    if (!this.isNoU(xUsername)) {
      resultUrl = resultUrl + '&xUsername=' + this.encode(xUsername.toString(), enc);
    }

    if (!this.isNoU(xOrigUserName)) {
      resultUrl = resultUrl + '&xOrigUserName=' + this.encode(xOrigUserName.toString(), enc);
    }

    if (!this.isNullOrEmptyString(program)) {
      resultUrl = resultUrl + '&program=' + program;
    }

    if (!this.isNoU(custId)) {
      resultUrl = resultUrl + '&custid=' + this.encode(custId.toString(), enc);
    }

    if (!this.isNoU(eid)) {
      resultUrl = resultUrl + '&eid=' + this.encode(eid.toString(), enc);
    }

    if (!this.isNoU(eventtype)) {
      resultUrl = resultUrl + '&eventtype=' + this.encode(eventtype.toString(), enc);
    }

    if (!this.isNoU(tabtitle)) {
      if (!this.isNullOrEmptyString(tabtitle)) {
        resultUrl = resultUrl + '&tabtitle=' + this.encode(tabtitle, enc);
      } else {
        resultUrl = resultUrl + '&tabtitle=';
      }
    }

    if (!this.isNoU(useCoreView)) {
      resultUrl = resultUrl + '&UseCoreView=' + this.encode(useCoreView.toString(), enc);
    }
    

    return resultUrl;
  }

  private encode(data: string, encode: boolean): string {
    if (encode) {
      return btoa(data);
    } else {
      return data;
    }
  }
}
